import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import api from '../lib/api';
import { fmtHours } from '../lib/formatters';
import EmployeeLink from '../components/EmployeeLink';
import { useAuth } from '../hooks/useAuth';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { UserPlus, ChevronUp, ChevronDown, X, Search, User, Trash2, Bell } from 'lucide-react';

// ---- helpers ----
function fmtRelative(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ---- schemas ----
const inviteSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Enter a valid email'),
  role: z.enum(['ADMIN', 'MANAGER', 'EMPLOYEE'], { required_error: 'Select a role' }),
  hourlyRate: z
    .string()
    .min(1, 'Hourly rate is required')
    .refine((v) => !isNaN(Number(v)) && Number(v) > 0, 'Must be a positive number'),
});

// ---- helpers ----
function StatusBadge({ online, tracking }) {
  if (tracking) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
      Tracking
    </span>
  );
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        online ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-green-500' : 'bg-gray-400'}`} />
      {online ? 'Online' : 'Offline'}
    </span>
  );
}

function SortIcon({ column, sortKey, direction }) {
  if (sortKey !== column) return <ChevronUp className="w-3 h-3 text-gray-300" />;
  return direction === 'asc' ? (
    <ChevronUp className="w-3 h-3 text-blue-500" />
  ) : (
    <ChevronDown className="w-3 h-3 text-blue-500" />
  );
}

const testNotifSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  message: z.string().min(1, 'Message is required'),
  action: z.enum(['none', 'url']),
  url: z.string().optional(),
}).refine((d) => d.action !== 'url' || (d.url && d.url.length > 0), {
  message: 'URL is required when action is URL',
  path: ['url'],
});

// ---- test notification modal ----
function TestNotifModal({ employee, onClose }) {
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(testNotifSchema),
    defaultValues: { action: 'none' },
  });
  const action = watch('action');
  const [sent, setSent] = useState(false);

  const onSubmit = async (values) => {
    await api.post('/api/notifications/test', {
      userId: employee.id,
      title: values.title,
      message: values.message,
      url: values.action === 'url' ? values.url : '',
    });
    setSent(true);
    setTimeout(onClose, 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Send Test Notification</h2>
          <button onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        {sent ? (
          <div className="p-6 text-center">
            <p className="text-green-600 font-medium">Notification sent to {employee.name}!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4" noValidate>
            <p className="text-sm text-gray-500">Sending to: <span className="font-medium text-gray-800">{employee.name}</span></p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input {...register('title')} className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.title ? 'border-red-400' : 'border-gray-300'}`} placeholder="e.g. Reminder" />
              {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea {...register('message')} rows={3} className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${errors.message ? 'border-red-400' : 'border-gray-300'}`} placeholder="Your message here..." />
              {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Click Action</label>
              <select {...register('action')} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="none">No action (dismiss only)</option>
                <option value="url">Open URL in browser</option>
              </select>
            </div>
            {action === 'url' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                <input {...register('url')} className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.url ? 'border-red-400' : 'border-gray-300'}`} placeholder="https://..." />
                {errors.url && <p className="mt-1 text-xs text-red-600">{errors.url.message}</p>}
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">Cancel</button>
              <button type="submit" disabled={isSubmitting} className="flex-1 py-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium transition-colors">
                {isSubmitting ? 'Sending...' : 'Send Notification'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---- broadcast modal ----
const broadcastSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  message: z.string().min(1, 'Message is required'),
});

function BroadcastModal({ onClose, discordUsers }) {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(broadcastSchema),
  });
  const [taggedIds, setTaggedIds] = useState([]);
  const [sent, setSent] = useState(null); // null | number

  const toggleTag = (id) => {
    setTaggedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const onSubmit = async (values) => {
    const res = await api.post('/api/notifications/broadcast', {
      title: values.title,
      message: values.message,
      taggedUserIds: taggedIds,
    });
    setSent(res.data.data.sent);
    setTimeout(onClose, 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Send Announcement</h2>
          <button onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        {sent !== null ? (
          <div className="p-6 text-center">
            <p className="text-green-600 font-medium">Sent to {sent} employee{sent !== 1 ? 's' : ''}!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4" noValidate>
            {discordUsers.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tag on Discord <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="flex flex-wrap gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50 min-h-[44px]">
                  {discordUsers.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => toggleTag(u.id)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors border ${
                        taggedIds.includes(u.id)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      {u.name}
                    </button>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  Leave empty to send without @mentions. Message still posts to Discord.
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                {...register('title')}
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.title ? 'border-red-400' : 'border-gray-300'}`}
                placeholder="e.g. Server maintenance tonight"
              />
              {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                {...register('message')}
                rows={3}
                className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${errors.message ? 'border-red-400' : 'border-gray-300'}`}
                placeholder="Your announcement..."
              />
              {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message.message}</p>}
            </div>
            <p className="text-xs text-gray-400">
              In-app notification sent to <strong>all employees</strong>. Discord message always fires to the notify channel.
            </p>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={isSubmitting} className="flex-1 py-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium transition-colors">
                {isSubmitting ? 'Sending...' : 'Send Announcement'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---- confirm delete modal ----
function ConfirmDeleteModal({ employee, onConfirm, onClose, isDeleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Remove Member</h2>
          <button onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to remove <span className="font-semibold text-gray-900">{employee.name}</span> from the team?
            This will permanently delete their account and all associated data.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isDeleting}
              className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className="flex-1 py-2 px-4 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-medium transition-colors"
            >
              {isDeleting ? 'Removing...' : 'Remove Member'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- invite modal ----
function InviteModal({ onClose, onSuccess }) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(inviteSchema) });

  const onSubmit = async (values) => {
    try {
      await api.post('/api/users', {
        ...values,
        hourlyRate: Number(values.hourlyRate),
      });
      onSuccess();
    } catch (err) {
      setError('root', {
        message: err?.response?.data?.error || 'Failed to invite employee.',
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">Invite Employee</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4" noValidate>
          {errors.root && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {errors.root.message}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              {...register('name')}
              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
              placeholder="Jane Smith"
            />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              {...register('email')}
              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
              placeholder="jane@company.com"
            />
            {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              {...register('role')}
              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white ${errors.role ? 'border-red-400' : 'border-gray-300'}`}
            >
              <option value="">Select role...</option>
              <option value="EMPLOYEE">Employee</option>
              <option value="MANAGER">Manager</option>
              <option value="ADMIN">Admin</option>
            </select>
            {errors.role && <p className="mt-1 text-xs text-red-600">{errors.role.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hourly Rate ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              {...register('hourlyRate')}
              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.hourlyRate ? 'border-red-400' : 'border-gray-300'}`}
              placeholder="25.00"
            />
            {errors.hourlyRate && (
              <p className="mt-1 text-xs text-red-600">{errors.hourlyRate.message}</p>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium transition-colors"
            >
              {isSubmitting ? 'Inviting...' : 'Invite Employee'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function fmtPlatform(platform, arch) {
  const names = { darwin: 'macOS', win32: 'Windows', linux: 'Linux' };
  const label = names[platform] || platform;
  return arch ? `${label} (${arch})` : label;
}

// ---- employee detail drawer ----
function EmployeeDrawer({ employee, onClose, onRemove, onTestNotif, isAdmin, isSelf, managers }) {
  const queryClient = useQueryClient();
  const { timezone: tz, timeFormat } = useDisplaySettings();
  const [discordId, setDiscordId] = useState(employee.discordUserId || '');
  const [discordSaving, setDiscordSaving] = useState(false);
  const [discordSaved, setDiscordSaved] = useState(false);
  const [managerId, setManagerId] = useState(employee.managerId || '');
  const [managerSaving, setManagerSaving] = useState(false);
  const [managerSaved, setManagerSaved] = useState(false);
  const [role, setRole] = useState(employee.role || 'EMPLOYEE');
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleSaved, setRoleSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['employee-detail', employee.id],
    queryFn: async () => {
      const [timelineRes, screenshotsRes, routineRes] = await Promise.all([
        api.get(`/api/timelogs?userId=${employee.id}&date=today`),
        api.get(`/api/screenshots?userId=${employee.id}&limit=6`),
        api.get(`/api/users/${employee.id}/routine`).catch(() => ({ data: { data: [] } })),
      ]);
      return {
        timeline: timelineRes.data?.data || [],
        screenshots: screenshotsRes.data?.data || [],
        routine: routineRes.data?.data || [],
      };
    },
    enabled: !!employee.id,
  });

  const saveDiscordId = async () => {
    setDiscordSaving(true);
    try {
      await api.put(`/api/users/${employee.id}`, { discordUserId: discordId.trim() || null });
      queryClient.invalidateQueries({ queryKey: ['team'] });
      setDiscordSaved(true);
      setTimeout(() => setDiscordSaved(false), 2000);
    } finally {
      setDiscordSaving(false);
    }
  };

  const saveManager = async () => {
    setManagerSaving(true);
    try {
      await api.put(`/api/users/${employee.id}`, { managerId: managerId || null });
      queryClient.invalidateQueries({ queryKey: ['team'] });
      setManagerSaved(true);
      setTimeout(() => setManagerSaved(false), 2000);
    } finally {
      setManagerSaving(false);
    }
  };

  const saveRole = async () => {
    setRoleSaving(true);
    try {
      await api.put(`/api/users/${employee.id}`, { role });
      queryClient.invalidateQueries({ queryKey: ['team', 'managers'] });
      setRoleSaved(true);
      setTimeout(() => setRoleSaved(false), 2000);
    } finally {
      setRoleSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end">
      <div className="fixed inset-0 bg-black bg-opacity-30" onClick={onClose} />
      <div className="relative z-10 w-full max-w-sm bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 text-blue-700 font-semibold">
              {employee.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <EmployeeLink userId={employee.id} name={employee.name} className="text-sm font-semibold text-gray-900" />
              <p className="text-xs text-gray-500">{employee.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {isLoading ? (
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded animate-pulse" />
              <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
            </div>
          ) : (
            <>
              {/* Info */}
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Info</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Role</span>
                    <span className="font-medium text-gray-900">{employee.role}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Rate</span>
                    <span className="font-medium text-gray-900">${employee.hourlyRate}/hr</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Hours Today</span>
                    <span className="font-medium text-gray-900">{fmtHours(employee.hoursToday)}</span>
                  </div>
                </div>
              </div>

              {/* Device Info */}
              {employee.appMeta?.version && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Device Info</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">App version</span>
                      <span className="font-medium text-gray-900">v{employee.appMeta.version}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Platform</span>
                      <span className="font-medium text-gray-900">{fmtPlatform(employee.appMeta.platform, employee.appMeta.arch)}</span>
                    </div>
                    {employee.appMeta.os && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">OS</span>
                        <span className="font-medium text-gray-900 text-xs">{employee.appMeta.os}</span>
                      </div>
                    )}
                    {employee.appMeta.locale && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Locale</span>
                        <span className="font-medium text-gray-900">{employee.appMeta.locale}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Work Routine - Average */}
              {data?.routine && data.routine.length > 0 && (() => {
                // Calculate average start and end times across all days
                const avgStartMins = Math.round(
                  data.routine.reduce((sum, day) => sum + day.avgStartMins, 0) / data.routine.length
                );
                const avgEndMins = Math.round(
                  data.routine.reduce((sum, day) => sum + day.avgEndMins, 0) / data.routine.length
                );

                const startHours = Math.floor(avgStartMins / 60);
                const startMins = avgStartMins % 60;
                const endHours = Math.floor(avgEndMins / 60);
                const endMins = avgEndMins % 60;

                const startTime12h = startHours % 12 || 12;
                const endTime12h = endHours % 12 || 12;
                const startPeriod = startHours >= 12 ? 'PM' : 'AM';
                const endPeriod = endHours >= 12 ? 'PM' : 'AM';

                const displayStart = `${startTime12h}:${String(startMins).padStart(2, '0')} ${startPeriod}`;
                const displayEnd = `${endTime12h}:${String(endMins).padStart(2, '0')} ${endPeriod}`;

                return (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Avg Work Hours</p>
                    <div className="bg-blue-50 rounded-lg px-4 py-3 border border-blue-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Typical:</span>
                        <span className="text-sm font-semibold text-blue-700">
                          {displayStart} → {displayEnd}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">Based on {data.routine.length} work days</p>
                    </div>
                  </div>
                );
              })()}

              {/* Timeline */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                  Today's Timeline
                </p>
                {data?.timeline?.length === 0 ? (
                  <p className="text-sm text-gray-400">No time logged today.</p>
                ) : (
                  <div className="space-y-2">
                    {data?.timeline?.map((log) => (
                      <div key={log.id} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-gray-600 truncate">{log.task?.title || log.project?.name || '—'}</span>
                        <span className="text-gray-500 ml-2 flex-shrink-0">
                          {log.startTime ? new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: tz, hour12: timeFormat === '12h' }) : '--'} -{' '}
                          {log.endTime ? new Date(log.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone: tz, hour12: timeFormat === '12h' }) : 'now'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Screenshots */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                  Recent Screenshots
                </p>
                {data?.screenshots?.length === 0 ? (
                  <p className="text-sm text-gray-400">No screenshots yet.</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {data?.screenshots?.map((ss) => (
                      <div key={ss.id} className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
                        <img
                          src={ss.imageUrl}
                          alt="screenshot"
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        {isAdmin && (
          <div className="px-5 py-4 border-t border-gray-200 space-y-3">
            {/* Role */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
              <div className="flex gap-2">
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="flex-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="EMPLOYEE">Employee</option>
                  <option value="MANAGER">Manager</option>
                  <option value="ADMIN">Admin</option>
                </select>
                <button
                  type="button"
                  onClick={saveRole}
                  disabled={roleSaving}
                  className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-xs font-medium transition-colors"
                >
                  {roleSaving ? '...' : roleSaved ? 'Saved!' : 'Save'}
                </button>
              </div>
            </div>

            {/* Manager Assignment */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Manager</label>
              <div className="flex gap-2">
                <select
                  value={managerId}
                  onChange={(e) => setManagerId(e.target.value)}
                  className="flex-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="">Unassigned</option>
                  {(managers || []).map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={saveManager}
                  disabled={managerSaving}
                  className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-xs font-medium transition-colors"
                >
                  {managerSaving ? '...' : managerSaved ? 'Saved!' : 'Save'}
                </button>
              </div>
            </div>

            {/* Discord User ID */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Discord User ID</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={discordId}
                  onChange={(e) => setDiscordId(e.target.value)}
                  placeholder="e.g. 123456789012345678"
                  className="flex-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={saveDiscordId}
                  disabled={discordSaving}
                  className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-xs font-medium transition-colors"
                >
                  {discordSaving ? '...' : discordSaved ? 'Saved!' : 'Save'}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-400">Right-click member in Discord → Copy User ID</p>
            </div>

            <button
              onClick={onTestNotif}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 text-sm font-medium transition-colors"
            >
              <Bell className="w-4 h-4" />
              Send Test Notification
            </button>
            {!isSelf && (
              <button
                onClick={onRemove}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Remove from Team
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- main page ----
export default function Team() {
  const queryClient = useQueryClient();
  const { user: currentUser, isAdmin } = useAuth();
  const isManager = currentUser?.role === 'MANAGER';
  const [showInvite, setShowInvite] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [testNotifTarget, setTestNotifTarget] = useState(null);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const deleteMutation = useMutation({
    mutationFn: (userId) => api.delete(`/api/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team'] });
      setDeleteTarget(null);
      setSelectedEmployee(null);
    },
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['team'],
    queryFn: async () => {
      const res = await api.get('/api/users');
      return res.data?.data || [];
    },
    refetchInterval: 30000,
  });

  // Fetch all managers for the dropdown
  const { data: managers } = useQuery({
    queryKey: ['managers'],
    queryFn: async () => {
      const res = await api.get('/api/users?role=MANAGER');
      return res.data?.data || [];
    },
  });

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const discordUsers = useMemo(() => (data || []).filter((u) => u.discordUserId), [data]);

  const filtered = useMemo(() => {
    let list = data || [];

    // MANAGER only sees their assigned employees
    if (isManager) {
      list = list.filter((u) => u.managerId === currentUser?.id);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
      );
    }
    if (statusFilter === 'active') list = list.filter((u) => u.isOnline);
    if (statusFilter === 'inactive') list = list.filter((u) => !u.isOnline);

    list = [...list].sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [data, search, statusFilter, sortKey, sortDir, isManager, currentUser?.id]);

  const handleInviteSuccess = () => {
    setShowInvite(false);
    queryClient.invalidateQueries({ queryKey: ['team'] });
  };

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <p className="text-red-600 font-medium">Failed to load team data.</p>
        <button onClick={() => refetch()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-500">{filtered.length} member{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBroadcast(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg border border-gray-300 transition-colors"
            >
              <Bell className="w-4 h-4" />
              Send Announcement
            </button>
            <button
              onClick={() => setShowInvite(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Invite Employee
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
          {['all', 'active', 'inactive'].map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                statusFilter === f ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                {[
                  { key: 'name', label: 'Name' },
                  { key: 'role', label: 'Role' },
                  { key: 'hourlyRate', label: 'Rate' },
                  { key: 'managerId', label: 'Manager' },
                  { key: 'hoursToday', label: 'Today' },
                  { key: 'hoursWeek', label: 'This Week' },
                  { key: 'lastActiveAt', label: 'Last Seen' },
                  { key: 'isOnline', label: 'Status' },
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer hover:text-gray-900 whitespace-nowrap"
                    onClick={() => handleSort(key)}
                  >
                    <div className="flex items-center gap-1">
                      {label}
                      <SortIcon column={key} sortKey={sortKey} direction={sortDir} />
                    </div>
                  </th>
                ))}
                {isAdmin && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${60 + j * 5}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                : filtered.length === 0 ? (
                    <tr>
                      <td colSpan={isAdmin ? 9 : 8} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <User className="w-8 h-8 text-gray-300" />
                          <p className="text-sm text-gray-400">No team members found.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((member) => (
                      <tr
                        key={member.id}
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedEmployee(member)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex-shrink-0">
                              {member.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <div>
                              <EmployeeLink userId={member.id} name={member.name} className="font-medium text-gray-900" />
                              <p className="text-xs text-gray-400">{member.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{member.role}</td>
                        <td className="px-4 py-3 text-gray-600">${member.hourlyRate}/hr</td>
                        <td className="px-4 py-3 text-gray-600 text-sm">
                          {member.managerId ? (managers || []).find(m => m.id === member.managerId)?.name || '—' : '—'}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {fmtHours(member.hoursToday)}
                          {member.currentProject && (
                            <p className="text-xs text-blue-600 font-normal truncate max-w-[120px]">
                              {member.currentProject}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {fmtHours(member.hoursWeek)}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {fmtRelative(member.lastActiveAt)}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge online={member.isOnline} tracking={!!member.currentProject} />
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3 text-right">
                            {member.id !== currentUser?.id && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setDeleteTarget(member); }}
                                className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                title="Remove member"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showInvite && (
        <InviteModal onClose={() => setShowInvite(false)} onSuccess={handleInviteSuccess} />
      )}
      {showBroadcast && (
        <BroadcastModal onClose={() => setShowBroadcast(false)} discordUsers={discordUsers} />
      )}
      {selectedEmployee && (
        <EmployeeDrawer
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
          onRemove={() => setDeleteTarget(selectedEmployee)}
          onTestNotif={() => setTestNotifTarget(selectedEmployee)}
          isAdmin={isAdmin}
          isSelf={selectedEmployee.id === currentUser?.id}
          managers={managers}
        />
      )}
      {testNotifTarget && (
        <TestNotifModal
          employee={testNotifTarget}
          onClose={() => setTestNotifTarget(null)}
        />
      )}
      {deleteTarget && (
        <ConfirmDeleteModal
          employee={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
          isDeleting={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
